/**
 * AnalyticsDashboardPage Component
 *
 * Surfaces P3 analytics backend: Three R's Classification, Outcome Correlation,
 * Calibration Monitor, Health Predictor, and System Intelligence Synthesis.
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Table, Spinner, Select, Button, Alert } from 'flowbite-react';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  getHealthPredictions,
  runHealthPrediction,
  getClassifications,
  triggerClassification,
  getCalibrationScores,
  runCalibration,
  getCorrelations,
  runCorrelation,
  getIntelligenceSynthesis,
  runIntelligenceSynthesis,
  getIntelligenceInsights,
  type HealthPrediction,
  type ThreeRsClassification,
  type CalibrationScore,
  type OutcomeCorrelation,
  type IntelligenceSynthesis,
  type IntelligenceInsight,
  type CrossEngineCorrelation,
} from '../api/analytics';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getHealthScoreColor(score: number): string {
  if (score > 75) return 'text-green-600';
  if (score > 50) return 'text-yellow-600';
  if (score > 25) return 'text-orange-600';
  return 'text-red-600';
}

function getHealthScoreBg(score: number): string {
  if (score > 75) return 'bg-green-100';
  if (score > 50) return 'bg-yellow-100';
  if (score > 25) return 'bg-orange-100';
  return 'bg-red-100';
}

function getStatusBadgeColor(status: string): 'success' | 'warning' | 'failure' | 'gray' {
  switch (status) {
    case 'healthy': return 'success';
    case 'warning': return 'warning';
    case 'at_risk': return 'failure';
    case 'critical': return 'failure';
    default: return 'gray';
  }
}

function getGradeBadgeColor(grade: string): 'success' | 'info' | 'warning' | 'failure' | 'gray' {
  switch (grade) {
    case 'A': return 'success';
    case 'B': return 'info';
    case 'C': return 'warning';
    case 'D': return 'failure';
    case 'F': return 'failure';
    default: return 'gray';
  }
}

function getSeverityBadgeColor(severity: string): 'info' | 'warning' | 'failure' {
  switch (severity) {
    case 'info': return 'info';
    case 'warning': return 'warning';
    case 'critical': return 'failure';
    default: return 'info';
  }
}

function TrendIcon({ trend }: { trend: string }) {
  if (trend === 'improving') return <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />;
  if (trend === 'declining') return <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />;
  return <MinusIcon className="w-5 h-5 text-gray-400" />;
}

// =============================================================================
// TAB COMPONENTS
// =============================================================================

// --- Agent Health Tab ---
function AgentHealthTab({
  predictions,
  isLoading,
  isRunning,
  onRunHealthCheck,
}: {
  predictions: HealthPrediction[];
  isLoading: boolean;
  isRunning: boolean;
  onRunHealthCheck: () => void;
}) {
  const [sortBy, setSortBy] = useState<'score' | 'name' | 'status'>('score');

  const sorted = [...predictions].sort((a, b) => {
    if (sortBy === 'score') return b.Health_Score - a.Health_Score;
    if (sortBy === 'name') return a.Agent_Name.localeCompare(b.Agent_Name);
    const statusOrder = { critical: 0, at_risk: 1, warning: 2, healthy: 3 };
    return (statusOrder[a.Health_Status] ?? 4) - (statusOrder[b.Health_Status] ?? 4);
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Agent Health Overview</h2>
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} sizing="sm">
            <option value="score">Sort by Score</option>
            <option value="name">Sort by Name</option>
            <option value="status">Sort by Status</option>
          </Select>
        </div>
        <Button size="sm" onClick={onRunHealthCheck} disabled={isRunning}>
          {isRunning ? <Spinner size="sm" className="mr-2" /> : <PlayIcon className="w-4 h-4 mr-2" />}
          Run Health Check
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : sorted.length === 0 ? (
        <Card>
          <p className="text-gray-500 text-center">No health predictions available. Run a health check to get started.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((agent) => (
            <Card key={agent.Agent_Name}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{agent.Agent_Name}</h3>
                  <Badge color={getStatusBadgeColor(agent.Health_Status)} className="mt-1">
                    {agent.Health_Status.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <TrendIcon trend={agent.Trend} />
                  <div className={`text-2xl font-bold ${getHealthScoreColor(agent.Health_Score)} ${getHealthScoreBg(agent.Health_Score)} rounded-full w-12 h-12 flex items-center justify-center`}>
                    {agent.Health_Score}
                  </div>
                </div>
              </div>

              {agent.Signals && agent.Signals.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">Signals</p>
                  <div className="grid grid-cols-2 gap-1">
                    {agent.Signals.map((signal) => (
                      <div key={signal.Signal_Name} className="flex justify-between text-xs">
                        <span className="text-gray-600 truncate mr-1">{signal.Signal_Name}</span>
                        <span className={`font-medium ${getHealthScoreColor(signal.Score)}`}>{signal.Score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Three R's Classification Tab ---
function ThreeRsTab({
  classifications,
  isLoading,
  isRunning,
  onRunClassification,
}: {
  classifications: ThreeRsClassification[];
  isLoading: boolean;
  isRunning: boolean;
  onRunClassification: () => void;
}) {
  const replace = classifications.filter((c) => c.Category === 'REPLACE');
  const remediate = classifications.filter((c) => c.Category === 'REMEDIATE');
  const reward = classifications.filter((c) => c.Category === 'REWARD');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Three R's Classification</h2>
        <Button size="sm" onClick={onRunClassification} disabled={isRunning}>
          {isRunning ? <Spinner size="sm" className="mr-2" /> : <PlayIcon className="w-4 h-4 mr-2" />}
          Run Classification
        </Button>
      </div>

      {/* Summary bar */}
      <div className="flex gap-4 mb-6">
        <Card className="flex-1">
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{replace.length}</p>
            <p className="text-sm text-gray-500">Replace</p>
          </div>
        </Card>
        <Card className="flex-1">
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">{remediate.length}</p>
            <p className="text-sm text-gray-500">Remediate</p>
          </div>
        </Card>
        <Card className="flex-1">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{reward.length}</p>
            <p className="text-sm text-gray-500">Reward</p>
          </div>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : classifications.length === 0 ? (
        <Card>
          <p className="text-gray-500 text-center">No classifications available. Run classification to get started.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* REPLACE column */}
          <div>
            <h3 className="text-sm font-semibold text-red-700 mb-3 uppercase tracking-wide">Replace</h3>
            <div className="space-y-2">
              {replace.map((agent) => (
                <Card key={agent.Agent_Name} className="border-l-4 border-l-red-500">
                  <p className="font-medium text-sm text-gray-900">{agent.Agent_Name}</p>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>P{agent.Percentile}</span>
                    <span>BA: {agent.Batting_Average.toFixed(3)}</span>
                  </div>
                </Card>
              ))}
              {replace.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No agents</p>
              )}
            </div>
          </div>

          {/* REMEDIATE column */}
          <div>
            <h3 className="text-sm font-semibold text-yellow-700 mb-3 uppercase tracking-wide">Remediate</h3>
            <div className="space-y-2">
              {remediate.map((agent) => (
                <Card key={agent.Agent_Name} className="border-l-4 border-l-yellow-500">
                  <p className="font-medium text-sm text-gray-900">{agent.Agent_Name}</p>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>P{agent.Percentile}</span>
                    <span>BA: {agent.Batting_Average.toFixed(3)}</span>
                  </div>
                </Card>
              ))}
              {remediate.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No agents</p>
              )}
            </div>
          </div>

          {/* REWARD column */}
          <div>
            <h3 className="text-sm font-semibold text-green-700 mb-3 uppercase tracking-wide">Reward</h3>
            <div className="space-y-2">
              {reward.map((agent) => (
                <Card key={agent.Agent_Name} className="border-l-4 border-l-green-500">
                  <p className="font-medium text-sm text-gray-900">{agent.Agent_Name}</p>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>P{agent.Percentile}</span>
                    <span>BA: {agent.Batting_Average.toFixed(3)}</span>
                  </div>
                </Card>
              ))}
              {reward.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No agents</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Calibration Monitor Tab ---
function CalibrationTab({
  scores,
  isLoading,
  isRunning,
  onRunCalibration,
}: {
  scores: CalibrationScore[];
  isLoading: boolean;
  isRunning: boolean;
  onRunCalibration: () => void;
}) {
  const avgBrier = scores.length > 0
    ? (scores.reduce((sum, s) => sum + s.Brier_Score, 0) / scores.length).toFixed(4)
    : 'N/A';

  const gradeDistribution = scores.reduce<Record<string, number>>((acc, s) => {
    acc[s.Grade] = (acc[s.Grade] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Calibration Monitor</h2>
        <Button size="sm" onClick={onRunCalibration} disabled={isRunning}>
          {isRunning ? <Spinner size="sm" className="mr-2" /> : <PlayIcon className="w-4 h-4 mr-2" />}
          Run Calibration
        </Button>
      </div>

      {/* Summary */}
      <div className="flex gap-4 mb-6">
        <Card className="flex-1">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{avgBrier}</p>
            <p className="text-sm text-gray-500">Avg Brier Score</p>
          </div>
        </Card>
        {['A', 'B', 'C', 'D', 'F'].map((grade) => (
          <Card key={grade} className="flex-1">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{gradeDistribution[grade] || 0}</p>
              <p className="text-sm text-gray-500">Grade {grade}</p>
            </div>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : scores.length === 0 ? (
        <Card>
          <p className="text-gray-500 text-center">No calibration scores available. Run calibration to get started.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <Table striped>
            <Table.Head>
              <Table.HeadCell>Agent</Table.HeadCell>
              <Table.HeadCell>Brier Score</Table.HeadCell>
              <Table.HeadCell>Grade</Table.HeadCell>
              <Table.HeadCell>Overconfidence Ratio</Table.HeadCell>
              <Table.HeadCell>Drift</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {scores.map((score) => (
                <Table.Row key={score.Agent_Name}>
                  <Table.Cell className="font-medium text-gray-900">{score.Agent_Name}</Table.Cell>
                  <Table.Cell>{score.Brier_Score.toFixed(4)}</Table.Cell>
                  <Table.Cell>
                    <Badge color={getGradeBadgeColor(score.Grade)}>{score.Grade}</Badge>
                  </Table.Cell>
                  <Table.Cell>{score.Overconfidence_Ratio.toFixed(2)}</Table.Cell>
                  <Table.Cell>
                    {score.Drift_Detected && (
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" title="Drift detected" />
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}

// --- Outcome Correlations Tab ---
function CorrelationsTab({
  correlations,
  isLoading,
  isRunning,
  onRunCorrelation,
}: {
  correlations: OutcomeCorrelation[];
  isLoading: boolean;
  isRunning: boolean;
  onRunCorrelation: () => void;
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Outcome Correlations</h2>
        <Button size="sm" onClick={onRunCorrelation} disabled={isRunning}>
          {isRunning ? <Spinner size="sm" className="mr-2" /> : <PlayIcon className="w-4 h-4 mr-2" />}
          Compute Correlations
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : correlations.length === 0 ? (
        <Card>
          <p className="text-gray-500 text-center">No correlations available. Compute correlations to get started.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <Table striped>
            <Table.Head>
              <Table.HeadCell>Factor A</Table.HeadCell>
              <Table.HeadCell>Factor B</Table.HeadCell>
              <Table.HeadCell>Correlation</Table.HeadCell>
              <Table.HeadCell>Sample Size</Table.HeadCell>
              <Table.HeadCell>P-Value</Table.HeadCell>
              <Table.HeadCell>Significant</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {correlations.map((corr, idx) => {
                const isSignificant = corr.P_Value < 0.05;
                const coeffColor = corr.Correlation_Coefficient >= 0 ? 'text-green-600' : 'text-red-600';
                return (
                  <Table.Row key={idx} className={isSignificant ? 'bg-yellow-50' : ''}>
                    <Table.Cell className="font-medium text-gray-900">{corr.Factor_A}</Table.Cell>
                    <Table.Cell className="font-medium text-gray-900">{corr.Factor_B}</Table.Cell>
                    <Table.Cell>
                      <span className={`font-semibold ${coeffColor}`}>
                        {corr.Correlation_Coefficient.toFixed(4)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{corr.Sample_Size}</Table.Cell>
                    <Table.Cell>{corr.P_Value.toFixed(4)}</Table.Cell>
                    <Table.Cell>
                      {isSignificant && (
                        <Badge color="warning">p &lt; 0.05</Badge>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}

// --- System Intelligence Tab ---
function IntelligenceTab({
  synthesis,
  insights,
  isLoading,
  isRunning,
  onRunSynthesis,
}: {
  synthesis: IntelligenceSynthesis | null;
  insights: IntelligenceInsight[];
  isLoading: boolean;
  isRunning: boolean;
  onRunSynthesis: () => void;
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">System Intelligence</h2>
        <Button size="sm" onClick={onRunSynthesis} disabled={isRunning}>
          {isRunning ? <Spinner size="sm" className="mr-2" /> : <PlayIcon className="w-4 h-4 mr-2" />}
          Run Synthesis
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !synthesis ? (
        <Card>
          <p className="text-gray-500 text-center">No synthesis available. Run synthesis to generate system intelligence.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Narrative */}
          <Card>
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-semibold text-gray-900">Synthesis Narrative</h3>
              <Badge color="info">Confidence: {(synthesis.Confidence * 100).toFixed(0)}%</Badge>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{synthesis.Narrative}</p>
          </Card>

          {/* Cross-Engine Correlations */}
          {synthesis.Cross_Engine_Correlations && synthesis.Cross_Engine_Correlations.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Cross-Engine Correlations</h3>
              <div className="space-y-3">
                {synthesis.Cross_Engine_Correlations.map((corr: CrossEngineCorrelation, idx: number) => (
                  <Alert key={idx} color={getSeverityBadgeColor(corr.Severity)}>
                    <div>
                      <span className="font-medium">{corr.Engine_A} + {corr.Engine_B}:</span>{' '}
                      {corr.Finding}
                    </div>
                  </Alert>
                ))}
              </div>
            </div>
          )}

          {/* Insights */}
          {insights.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Insights</h3>
              <div className="space-y-2">
                {insights.map((insight) => (
                  <Card key={insight.Insight_ID}>
                    <div className="flex items-start gap-3">
                      <Badge color={getSeverityBadgeColor(insight.Severity)}>{insight.Severity}</Badge>
                      <div>
                        <p className="font-medium text-sm text-gray-900">{insight.Title}</p>
                        <p className="text-sm text-gray-600 mt-1">{insight.Description}</p>
                        <p className="text-xs text-gray-400 mt-1">Source: {insight.Engine_Source}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

type TabKey = 'health' | 'threeRs' | 'calibration' | 'correlations' | 'intelligence';

const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'health', label: 'Agent Health' },
  { key: 'threeRs', label: "Three R's" },
  { key: 'calibration', label: 'Calibration' },
  { key: 'correlations', label: 'Correlations' },
  { key: 'intelligence', label: 'Intelligence' },
];

export const AnalyticsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('health');

  // Loading states
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [loadingClassifications, setLoadingClassifications] = useState(false);
  const [loadingCalibration, setLoadingCalibration] = useState(false);
  const [loadingCorrelations, setLoadingCorrelations] = useState(false);
  const [loadingIntelligence, setLoadingIntelligence] = useState(false);

  // Running states (for trigger buttons)
  const [runningHealth, setRunningHealth] = useState(false);
  const [runningClassification, setRunningClassification] = useState(false);
  const [runningCalibration, setRunningCalibration] = useState(false);
  const [runningCorrelation, setRunningCorrelation] = useState(false);
  const [runningSynthesis, setRunningSynthesis] = useState(false);

  // Data states
  const [healthPredictions, setHealthPredictions] = useState<HealthPrediction[]>([]);
  const [classifications, setClassifications] = useState<ThreeRsClassification[]>([]);
  const [calibrationScores, setCalibrationScores] = useState<CalibrationScore[]>([]);
  const [correlations, setCorrelations] = useState<OutcomeCorrelation[]>([]);
  const [synthesis, setSynthesis] = useState<IntelligenceSynthesis | null>(null);
  const [intelligenceInsights, setIntelligenceInsights] = useState<IntelligenceInsight[]>([]);

  const clientId = user?.client_id;

  // --- Fetch functions ---

  const fetchHealth = useCallback(async () => {
    if (!clientId) return;
    setLoadingHealth(true);
    try {
      const response = await getHealthPredictions(clientId);
      if (response.success && response.data?.Records) {
        setHealthPredictions(response.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch health predictions:', error);
      toast.error('Failed to load health predictions');
    } finally {
      setLoadingHealth(false);
    }
  }, [clientId]);

  const fetchClassifications = useCallback(async () => {
    if (!clientId) return;
    setLoadingClassifications(true);
    try {
      const response = await getClassifications(clientId);
      if (response.success && response.data?.Records) {
        setClassifications(response.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch classifications:', error);
      toast.error('Failed to load classifications');
    } finally {
      setLoadingClassifications(false);
    }
  }, [clientId]);

  const fetchCalibration = useCallback(async () => {
    if (!clientId) return;
    setLoadingCalibration(true);
    try {
      const response = await getCalibrationScores(clientId);
      if (response.success && response.data?.Records) {
        setCalibrationScores(response.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch calibration scores:', error);
      toast.error('Failed to load calibration scores');
    } finally {
      setLoadingCalibration(false);
    }
  }, [clientId]);

  const fetchCorrelations = useCallback(async () => {
    if (!clientId) return;
    setLoadingCorrelations(true);
    try {
      const response = await getCorrelations(clientId);
      if (response.success && response.data?.Records) {
        setCorrelations(response.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch correlations:', error);
      toast.error('Failed to load correlations');
    } finally {
      setLoadingCorrelations(false);
    }
  }, [clientId]);

  const fetchIntelligence = useCallback(async () => {
    if (!clientId) return;
    setLoadingIntelligence(true);
    try {
      const [synthResponse, insightsResponse] = await Promise.all([
        getIntelligenceSynthesis(clientId),
        getIntelligenceInsights(clientId),
      ]);
      if (synthResponse.success && synthResponse.data?.Records?.[0]) {
        setSynthesis(synthResponse.data.Records[0]);
      }
      if (insightsResponse.success && insightsResponse.data?.Records) {
        setIntelligenceInsights(insightsResponse.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch intelligence:', error);
      toast.error('Failed to load intelligence synthesis');
    } finally {
      setLoadingIntelligence(false);
    }
  }, [clientId]);

  // --- Trigger functions ---

  const handleRunHealthCheck = useCallback(async () => {
    if (!clientId) return;
    setRunningHealth(true);
    try {
      const response = await runHealthPrediction(clientId, { Include_Signals: true });
      if (response.success) {
        toast.success('Health check completed');
        await fetchHealth();
      } else {
        toast.error(response.message || 'Health check failed');
      }
    } catch (error) {
      console.error('Failed to run health check:', error);
      toast.error('Failed to run health check');
    } finally {
      setRunningHealth(false);
    }
  }, [clientId, fetchHealth]);

  const handleRunClassification = useCallback(async () => {
    if (!clientId) return;
    setRunningClassification(true);
    try {
      const response = await triggerClassification(clientId, { Force_Refresh: true });
      if (response.success) {
        toast.success('Classification completed');
        await fetchClassifications();
      } else {
        toast.error(response.message || 'Classification failed');
      }
    } catch (error) {
      console.error('Failed to run classification:', error);
      toast.error('Failed to run classification');
    } finally {
      setRunningClassification(false);
    }
  }, [clientId, fetchClassifications]);

  const handleRunCalibration = useCallback(async () => {
    if (!clientId) return;
    setRunningCalibration(true);
    try {
      const response = await runCalibration(clientId);
      if (response.success) {
        toast.success('Calibration completed');
        await fetchCalibration();
      } else {
        toast.error(response.message || 'Calibration failed');
      }
    } catch (error) {
      console.error('Failed to run calibration:', error);
      toast.error('Failed to run calibration');
    } finally {
      setRunningCalibration(false);
    }
  }, [clientId, fetchCalibration]);

  const handleRunCorrelation = useCallback(async () => {
    if (!clientId) return;
    setRunningCorrelation(true);
    try {
      const response = await runCorrelation(clientId);
      if (response.success) {
        toast.success('Correlations computed');
        await fetchCorrelations();
      } else {
        toast.error(response.message || 'Correlation computation failed');
      }
    } catch (error) {
      console.error('Failed to compute correlations:', error);
      toast.error('Failed to compute correlations');
    } finally {
      setRunningCorrelation(false);
    }
  }, [clientId, fetchCorrelations]);

  const handleRunSynthesis = useCallback(async () => {
    if (!clientId) return;
    setRunningSynthesis(true);
    try {
      const response = await runIntelligenceSynthesis(clientId, { Depth: 'standard' });
      if (response.success) {
        toast.success('Synthesis completed');
        await fetchIntelligence();
      } else {
        toast.error(response.message || 'Synthesis failed');
      }
    } catch (error) {
      console.error('Failed to run synthesis:', error);
      toast.error('Failed to run synthesis');
    } finally {
      setRunningSynthesis(false);
    }
  }, [clientId, fetchIntelligence]);

  // --- Fetch data on tab change ---

  useEffect(() => {
    if (!clientId) return;

    switch (activeTab) {
      case 'health':
        fetchHealth();
        break;
      case 'threeRs':
        fetchClassifications();
        break;
      case 'calibration':
        fetchCalibration();
        break;
      case 'correlations':
        fetchCorrelations();
        break;
      case 'intelligence':
        fetchIntelligence();
        break;
    }
  }, [activeTab, clientId, fetchHealth, fetchClassifications, fetchCalibration, fetchCorrelations, fetchIntelligence]);

  if (!clientId) {
    return (
      <div className="py-8">
        <Alert color="warning">
          <span>No client context available. Please select a client to view analytics.</span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          Agent health, classification, calibration, correlation analysis, and system intelligence.
        </p>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex -mb-px space-x-8">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'health' && (
        <AgentHealthTab
          predictions={healthPredictions}
          isLoading={loadingHealth}
          isRunning={runningHealth}
          onRunHealthCheck={handleRunHealthCheck}
        />
      )}
      {activeTab === 'threeRs' && (
        <ThreeRsTab
          classifications={classifications}
          isLoading={loadingClassifications}
          isRunning={runningClassification}
          onRunClassification={handleRunClassification}
        />
      )}
      {activeTab === 'calibration' && (
        <CalibrationTab
          scores={calibrationScores}
          isLoading={loadingCalibration}
          isRunning={runningCalibration}
          onRunCalibration={handleRunCalibration}
        />
      )}
      {activeTab === 'correlations' && (
        <CorrelationsTab
          correlations={correlations}
          isLoading={loadingCorrelations}
          isRunning={runningCorrelation}
          onRunCorrelation={handleRunCorrelation}
        />
      )}
      {activeTab === 'intelligence' && (
        <IntelligenceTab
          synthesis={synthesis}
          insights={intelligenceInsights}
          isLoading={loadingIntelligence}
          isRunning={runningSynthesis}
          onRunSynthesis={handleRunSynthesis}
        />
      )}
    </div>
  );
};

export default AnalyticsDashboardPage;
